/**
 * The invite window (XC-TEAM-INVITES Phase 1, mockup screen 2). Each email becomes a chip as it is
 * typed or pasted and is checked on the spot: a typo is red, someone already on the team or already
 * invited is greyed, an address outside the company gets a gentle note (never a block). The button
 * counts only the invites that will go out, and stays off while a typo is left.
 *
 * Owners and Admins send at once; a Member's invite waits for an Owner or Admin (T2), and a Member
 * may ask only for Member or Viewer. The server decides every one of these; the window only says
 * them first.
 */

import { Modal, ModalButton, ModalFooter } from '@/components/ui/Modal';
import { useAuth } from '@/contexts/AuthContext';
import { useSendInvites, useTeam } from '@/hooks/data/useTeamData';
import { MARKER_EXCLUDE_CLASS } from '@/lib/markerPrivacy';
import { teamErrorMessage } from '@/services/teamService';
import {
  MAX_EMAILS_PER_INVITE,
  ROLE_LINES,
  ROLE_NAMES,
  grantableRoles,
  type Team,
  type TeamRole,
} from '@/types/team';
import { cn } from '@/utils';
import { X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

const EMAIL = /^[^@\s,;<>]+@[^@\s,;<>]+\.[A-Za-z]{2,}$/;

export type ChipKind = 'new' | 'invalid' | 'member' | 'invited';

export interface Chip {
  email: string;
  kind: ChipKind;
  name?: string;
  outside?: boolean;
}

/** Why the server did not send to an address, in words. */
export function notSentWords(email: string, status: string, name: string | null): string {
  switch (status) {
    case 'just_sent':
      return `${email} got an invite a moment ago. Try again in a couple of minutes.`;
    case 'already_invited':
      return `${email} already has an open invite. Resend it from the Team page.`;
    case 'member':
      return `${name ?? email} is already on the team.`;
    case 'invalid':
      return `"${email}" isn't a full email address.`;
    default:
      return `${email} was not sent.`;
  }
}

/** Split what was typed or pasted into addresses (commas, semicolons, spaces, new lines). */
export function splitEmails(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((e) => e.trim().replace(/^<|>$/g, ''))
    .filter(Boolean);
}

function domainOf(email: string | undefined | null): string {
  return email && email.includes('@') ? email.split('@').pop()!.toLowerCase() : '';
}

/** Check one address against the team as the server will (format, already a member, invited). */
export function checkEmail(email: string, team: Team | undefined, ownDomain: string): Chip {
  const lower = email.toLowerCase();
  if (!EMAIL.test(lower)) return { email, kind: 'invalid' };
  const member = team?.members.find((m) => m.email.toLowerCase() === lower);
  if (member) return { email: lower, kind: 'member', name: member.display_name || member.email };
  const open = [...(team?.invited ?? []), ...(team?.requests ?? [])].find(
    (i) => i.email === lower && i.state !== 'expired',
  );
  if (open) return { email: lower, kind: 'invited' };
  return { email: lower, kind: 'new', outside: !!ownDomain && domainOf(lower) !== ownDomain };
}

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const viewerRole = (user?.role ?? 'viewer') as TeamRole;
  const manages = viewerRole === 'owner' || viewerRole === 'admin';
  const roles = grantableRoles(viewerRole);
  const team = useTeam(isOpen);
  const send = useSendInvites();
  const ownDomain = domainOf(user?.email);

  const [chips, setChips] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [role, setRole] = useState<TeamRole>('member');
  const [note, setNote] = useState('');
  const [notSent, setNotSent] = useState<string[]>([]);

  const checked = useMemo(
    () => chips.map((c) => checkEmail(c, team.data, ownDomain)),
    [chips, team.data, ownDomain],
  );
  const sendable = checked.filter((c) => c.kind === 'new');
  const hasTypo = checked.some((c) => c.kind === 'invalid');
  const outside = sendable.filter((c) => c.outside);
  const tooMany = sendable.length > MAX_EMAILS_PER_INVITE;
  const workspace = user?.tenant_name || 'your workspace';

  function commit(text: string) {
    const added = splitEmails(text);
    if (added.length === 0) return;
    setChips((prev) => {
      const seen = new Set(prev.map((p) => p.toLowerCase()));
      return [...prev, ...added.filter((a) => !seen.has(a.toLowerCase()))];
    });
    setDraft('');
  }

  function reset() {
    setChips([]);
    setDraft('');
    setRole('member');
    setNote('');
  }

  async function submit() {
    if (hasTypo || sendable.length === 0 || tooMany) return;
    setNotSent([]);
    try {
      const result = await send.mutateAsync({
        emails: sendable.map((c) => c.email),
        role,
        note: note || null,
      });
      if (result.requested > 0) {
        toast.success(
          `${result.requested === 1 ? 'Your request was' : `${result.requested} requests were`} sent to an Owner or Admin to approve.`,
        );
      } else if (result.sent > 0) {
        toast.success(
          `${result.sent} invite${result.sent === 1 ? '' : 's'} sent. We'll let you know when they join.`,
        );
      }
      // Anything the server did not send stays in the window, each with its reason (/ship blocker
      // r3: a silent "0 invites sent" success closed the window and lost what was typed).
      const left = result.results.filter((r) => r.status !== 'invited' && r.status !== 'requested');
      if (left.length === 0) {
        reset();
        onClose();
        return;
      }
      setChips(left.map((r) => r.email));
      setNotSent(left.map((r) => notSentWords(r.email, r.status, r.name ?? null)));
    } catch (err) {
      toast.error(teamErrorMessage(err));
    }
  }


  if (roles.length === 0) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={`Invite people to ${workspace}`} size="md">
        <p className="text-sm text-gray-300">
          Viewers can&apos;t invite people. Ask an Owner or Admin to invite them for you.
        </p>
        <ModalFooter>
          <ModalButton type="button" onClick={onClose}>
            Close
          </ModalButton>
        </ModalFooter>
      </Modal>
    );
  }

  const buttonLabel = manages
    ? `Send ${sendable.length > 1 ? `${sendable.length} invites` : 'invite'}`
    : 'Ask to invite';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Invite people to ${workspace}`}
      description={
        manages
          ? "They'll get an email with a link to join. You can change their role any time."
          : "An Owner or Admin approves new people first. We'll email them as soon as it's approved."
      }
      size="lg"
    >
      <div className="space-y-5">
        <div>
          <label htmlFor="invite-emails" className="block text-sm font-medium text-gray-300 mb-1.5">
            Emails
          </label>
          <div
            className={cn(
              'flex flex-wrap gap-1.5 p-2 rounded-lg bg-white/[0.06] border border-white/10',
              MARKER_EXCLUDE_CLASS,
            )}
          >
            {checked.map((c) => (
              <span
                key={c.email}
                data-testid={`chip-${c.kind}`}
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs',
                  c.kind === 'invalid' && 'bg-red-500/20 text-red-200 border border-red-400/40',
                  c.kind === 'new' && 'bg-brand-500/15 text-white',
                  (c.kind === 'member' || c.kind === 'invited') && 'bg-white/[0.06] text-gray-400',
                )}
              >
                {c.kind === 'member' ? `${c.name} · already on the team` : c.email}
                {c.kind === 'invited' && ' · already invited'}
                <button
                  type="button"
                  aria-label={`Remove ${c.email}`}
                  onClick={() => setChips((prev) => prev.filter((p) => p.toLowerCase() !== c.email.toLowerCase()))}
                  className="hover:text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              id="invite-emails"
              value={draft}
              placeholder={chips.length ? '' : `name@${ownDomain || 'company.com'}, paste a list if you like`}
              onChange={(e) => {
                const v = e.target.value;
                if (/[\s,;]$/.test(v)) commit(v);
                else setDraft(v);
              }}
              onPaste={(e) => {
                e.preventDefault();
                commit(draft + e.clipboardData.getData('text'));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commit(draft);
                } else if (e.key === 'Backspace' && !draft && chips.length) {
                  setChips((prev) => prev.slice(0, -1));
                }
              }}
              onBlur={() => commit(draft)}
              className="flex-1 min-w-[180px] bg-transparent text-sm text-white placeholder-gray-500 outline-none px-1 py-0.5"
            />
          </div>
          {checked
            .filter((c) => c.kind === 'invalid')
            .map((c) => (
              <p key={c.email} className="mt-1 text-xs text-red-300">
                &ldquo;{c.email}&rdquo; isn&apos;t a full email address. Fix it or remove it.
              </p>
            ))}
          {outside.map((c) => (
            <p key={c.email} className="mt-1 text-xs text-gray-400">
              {c.email} is outside {ownDomain}. That&apos;s fine for an accountant or partner;
              Viewer may fit better.
            </p>
          ))}
          {notSent.map((line) => (
            <p key={line} className="mt-1 text-xs text-amber-300">
              {line}
            </p>
          ))}
          {tooMany && (
            <p className="mt-1 text-xs text-red-300">
              At most {MAX_EMAILS_PER_INVITE} people at a time.
            </p>
          )}
        </div>

        <fieldset>
          <legend className="block text-sm font-medium text-gray-300 mb-1.5">Role</legend>
          <div className="space-y-1.5">
            {roles.map((r) => (
              <label
                key={r}
                className={cn(
                  'flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer',
                  role === r ? 'border-brand-500/50 bg-brand-500/10' : 'border-white/10',
                )}
              >
                <input
                  type="radio"
                  name="invite-role"
                  value={r}
                  checked={role === r}
                  onChange={() => setRole(r)}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm text-white">{ROLE_NAMES[r]}</span>
                  <span className="block text-xs text-gray-400">{ROLE_LINES[r]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <label htmlFor="invite-note" className="block text-sm font-medium text-gray-300 mb-1.5">
            {manages ? 'Add a note (optional)' : 'Why should they join? (shown to the admin)'}
          </label>
          <textarea
            id="invite-note"
            value={note}
            maxLength={1000}
            rows={2}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-3 py-2 bg-white/[0.06] border border-white/10 rounded-lg text-sm text-white"
          />
        </div>
      </div>

      <ModalFooter>
        <ModalButton type="button" onClick={onClose} disabled={send.isPending}>
          Cancel
        </ModalButton>
        <ModalButton
          type="button"
          variant="primary"
          loading={send.isPending}
          disabled={hasTypo || sendable.length === 0 || tooMany || send.isPending}
          onClick={() => void submit()}
        >
          {buttonLabel}
        </ModalButton>
      </ModalFooter>
    </Modal>
  );
};

export default InviteModal;
